// ABOUTME: Published lists (public share) API client - configs, api-keys, snapshots
// ABOUTME: Covers /published/* endpoints, including token-only public read paths

import type { ApiSuccessResponse } from '@alpha-finder/contracts/envelope';
import type {
  CreatedApiKey,
  CreatePublicationConfigRequest,
  PublicationConfig,
  PublishedApiKey,
  PublishedSnapshot,
  PublishedSnapshots,
  UpdatePublicationConfigRequest,
} from '@alpha-finder/contracts/published';
import { apiClient } from './client';

export const publishedApi = {
  async listConfigs(): Promise<PublicationConfig[]> {
    const response =
      await apiClient.get<ApiSuccessResponse<PublicationConfig[]>>('/published/configs');
    return response.data.data;
  },

  async getConfig(configId: string): Promise<PublicationConfig> {
    const response = await apiClient.get<ApiSuccessResponse<PublicationConfig>>(
      `/published/configs/${configId}`
    );
    return response.data.data;
  },

  async createConfig(data: CreatePublicationConfigRequest): Promise<PublicationConfig> {
    const response = await apiClient.post<ApiSuccessResponse<PublicationConfig>>(
      '/published/configs',
      data
    );
    return response.data.data;
  },

  async updateConfig(
    configId: string,
    data: UpdatePublicationConfigRequest
  ): Promise<PublicationConfig> {
    const response = await apiClient.patch<ApiSuccessResponse<PublicationConfig>>(
      `/published/configs/${configId}`,
      data
    );
    return response.data.data;
  },

  async deleteConfig(configId: string): Promise<PublicationConfig> {
    const response = await apiClient.delete<ApiSuccessResponse<PublicationConfig>>(
      `/published/configs/${configId}`
    );
    return response.data.data;
  },

  async rotateToken(configId: string): Promise<PublicationConfig> {
    const response = await apiClient.post<ApiSuccessResponse<PublicationConfig>>(
      `/published/configs/${configId}/rotate-token`
    );
    return response.data.data;
  },

  async listApiKeys(): Promise<PublishedApiKey[]> {
    const response =
      await apiClient.get<ApiSuccessResponse<PublishedApiKey[]>>('/published/api-keys');
    return response.data.data;
  },

  async createApiKey(label: string): Promise<CreatedApiKey> {
    const response = await apiClient.post<ApiSuccessResponse<CreatedApiKey>>(
      '/published/api-keys',
      { label }
    );
    return response.data.data;
  },

  async revokeApiKey(keyId: string): Promise<void> {
    await apiClient.delete(`/published/api-keys/${keyId}`);
  },

  async getSnapshots(configId: string, limit = 20, offset = 0): Promise<PublishedSnapshot[]> {
    const response = await apiClient.get<ApiSuccessResponse<PublishedSnapshots>>(
      `/published/configs/${configId}/snapshots?limit=${limit}&offset=${offset}`
    );
    return response.data.data.snapshots;
  },

  async getLatestByToken(shareToken: string): Promise<PublishedSnapshot | null> {
    const response = await apiClient.get<ApiSuccessResponse<PublishedSnapshot | null>>(
      `/published/${shareToken}/latest`
    );
    return response.data.data;
  },

  async getSnapshotsByToken(
    shareToken: string,
    limit = 20,
    offset = 0
  ): Promise<PublishedSnapshot[]> {
    const response = await apiClient.get<ApiSuccessResponse<PublishedSnapshots>>(
      `/published/${shareToken}/snapshots?limit=${limit}&offset=${offset}`
    );
    return response.data.data.snapshots;
  },
};
